import { addDays } from 'date-fns';
import searchConfig from '@/config/search';
import { create, createEmptyFilter, parse as parseFilterQuery } from '@/lib/search/filter';
import { getHighestAveragePrice, mapMicrositePropertyToSearchProperty } from '@/lib/search/mapSearchProperty';
import { SORT_COMPARATORS, sortProperties } from '@/lib/search/sort';

const SEARCH_PARAMS_STORAGE_KEY = 'searchFormParams';

export const useSearchStore = defineStore('searchStore', () => {
  // ### NECESSARY COMPOSABLES
  const { isMobileOrTablet } = useDevice();

  // ### TOP VARIABLES
  const EMPTY_DATE = {
    year: 0,
    month: 0,
    day: 0,
  };
  const MAX_NUM_NIGHTS = 30;
  const NUMBER_OF_GUESTS = toValue(isMobileOrTablet)
    ? searchConfig.defaultGuests.mobile
    : searchConfig.defaultGuests.desktop;

  // ### STATE
  const searchParams = ref({
    location: {},
    checkIn: EMPTY_DATE,
    checkOut: EMPTY_DATE,
    disabledUntil: {},
    disabledFrom: {},
    guests: NUMBER_OF_GUESTS,
    groupType: {},
    groupAge: [],
    phrase: '',
  });
  const isSearchActivated = ref(false);
  const mapSelectedProperty = ref(null);
  const properties = ref([]);
  const location = ref({});
  const sortOrderRank = ref(null);
  const viewOptions = ref({
    page: 1,
    sort: null,
    filter: createEmptyFilter(),
  });
  const filterCommitDeferred = ref(false);
  const pendingFilter = ref(null);
  const maxPriceAmongProperties = ref(0);

  // ### GETTERS
  const getSearchLocation = computed(() => {
    return searchParams.value.location;
  });
  const getSearchPhrase = computed(() => {
    return searchParams.value.phrase;
  });
  const getSearchCheckIn = computed(() => {
    return searchParams.value.checkIn;
  });
  const getSearchCheckOut = computed(() => {
    return searchParams.value.checkOut;
  });
  const getSearchDisabledUntil = computed(() => {
    return searchParams.value.disabledUntil;
  });
  const getSearchDisableFrom = computed(() => {
    return searchParams.value.disabledFrom;
  });
  const getSearchNumberOfGuests = computed(() => {
    return searchParams.value.guests;
  });
  const getSearchGroupType = computed(() => {
    return searchParams.value.groupType;
  });
  const getSearchGroupAge = computed(() => {
    return searchParams.value.groupAge;
  });
  const getIsSearchActivated = computed(() => {
    return isSearchActivated.value;
  });
  const getMapSelectedProperty = computed(() => {
    return mapSelectedProperty.value;
  });
  const getProperties = computed(() => {
    return properties.value;
  });
  const getInventoryLocation = computed(() => {
    return location.value;
  });
  const getSortOrderRank = computed(() => {
    return sortOrderRank.value;
  });
  const currentFilter = computed(() => {
    if (filterCommitDeferred.value && pendingFilter.value) {
      return pendingFilter.value;
    }

    return viewOptions.value.filter;
  });
  const filteredHWProperties = computed(() => {
    const filterPredicate = create(viewOptions.value.filter);
    const filtered = properties.value.filter(filterPredicate);
    return sortProperties(filtered, viewOptions.value.sort);
  });
  const filteredProperties = computed(() => filteredHWProperties.value);
  const maxPriceAvailable = computed(() => maxPriceAmongProperties.value || 0);
  const numFiltersApplied = computed(() => {
    const filters = viewOptions.value.filter;
    return Object.keys(filters).reduce((count, key) => {
      const value = filters[key];
      if (value === null || value === undefined || value === false) {
        return count;
      }
      if (Array.isArray(value)) {
        return value.length === 0 ? count : count + value.length;
      }
      return count + 1;
    }, 0);
  });
  const numCurrentFiltersApplied = computed(() => {
    const filters = currentFilter.value;
    return Object.keys(filters).reduce((count, key) => {
      const value = filters[key];
      if (value === null || value === undefined || value === false) {
        return count;
      }
      if (Array.isArray(value)) {
        return value.length === 0 ? count : count + value.length;
      }
      return count + 1;
    }, 0);
  });
  const districts = computed(() => {
    const districtMap = {};
    properties.value.forEach((property) => {
      (property.districts || []).forEach((district) => {
        districtMap[String(district.id)] = district.name;
      });
    });

    return Object.keys(districtMap).map((key) => ({
      value: key,
      label: districtMap[key],
    }));
  });
  const getViewOptions = computed(() => viewOptions.value);

  // ### ACTIONS
  function setSearchParam(key, value) {
    if (key === 'checkIn') {
      setSearchCheckIn(value);
      return;
    }

    if (Object.keys(searchParams.value).includes(key)) {
      searchParams.value[key] = value;
    }
  }

  function setSearchCheckIn(value) {
    searchParams.value.checkIn = value;

    const start = new Date(value.year, value.month - 1, value.day);
    const disabledUntil = addDays(start, 1);
    const disableFrom = addDays(start, MAX_NUM_NIGHTS);

    searchParams.value.disabledUntil = {
      year: disabledUntil.getFullYear(),
      month: disabledUntil.getMonth() + 1,
      day: disabledUntil.getDate(),
    };

    searchParams.value.disabledFrom = {
      year: disableFrom.getFullYear(),
      month: disableFrom.getMonth() + 1,
      day: disableFrom.getDate(),
    };
  }

  function setSearchCheckOut(value) {
    searchParams.value.checkOut = value;
  }

  function addSearchGroupAge(value) {
    searchParams.value.groupAge.push(value);
  }

  function setSearchActivated(value) {
    isSearchActivated.value = value;
  }

  function sendSearchActivatedTracking() {
    /** TODO Implement tracking here */
  }

  function setMapSelectedProperty(value) {
    mapSelectedProperty.value = value;
  }

  function setProperties(value) {
    properties.value = Array.isArray(value) ? value : [];

    // Keep the previous ceiling while inventory is cleared for a refetch so QuickFilters
    // do not thrash maxPrice in the URL (0 → real max → 0) and retrigger navigation.
    if (!properties.value.length) {
      return;
    }

    maxPriceAmongProperties.value = properties.value.reduce((maxPrice, property) => {
      const highest = getHighestAveragePrice(property.prices);
      return highest > maxPrice ? highest : maxPrice;
    }, 0);
  }

  function setLocation(value) {
    location.value = value || {};
  }

  function setSortOrderRank(value) {
    sortOrderRank.value = value || searchConfig.noSortOrderSegment;
  }

  function setPage(page) {
    viewOptions.value.page = Number(page) || 1;
  }

  function setSort(sort) {
    viewOptions.value.sort = sort in SORT_COMPARATORS ? sort : null;
  }

  function setFilter(filter) {
    viewOptions.value.filter = {
      ...createEmptyFilter(),
      ...filter,
    };
  }

  function applyFilter({
    filter,
    value,
  }) {
    let nextValue = value;
    if ((typeof nextValue === 'boolean' && nextValue === false) || (Array.isArray(nextValue) && nextValue.length === 0)) {
      nextValue = null;
    }
    if (viewOptions.value.filter[filter] === nextValue) {
      return;
    }
    viewOptions.value.filter[filter] = nextValue;
  }

  function clearFilter() {
    viewOptions.value.filter = createEmptyFilter();
  }

  function setFilterCommitDeferred(value) {
    filterCommitDeferred.value = Boolean(value);
  }

  function initPendingFilter() {
    pendingFilter.value = {
      ...createEmptyFilter(),
      ...viewOptions.value.filter,
      roomType: viewOptions.value.filter.roomType ? [...viewOptions.value.filter.roomType] : null,
      facility: viewOptions.value.filter.facility ? [...viewOptions.value.filter.facility] : null,
      propertyType: viewOptions.value.filter.propertyType ? [...viewOptions.value.filter.propertyType] : null,
      district: viewOptions.value.filter.district ? [...viewOptions.value.filter.district] : null,
    };
  }

  function updatePendingFilter({
    filter,
    value,
  }) {
    if (!pendingFilter.value) {
      return;
    }

    let nextValue = value;
    if ((typeof nextValue === 'boolean' && nextValue === false) || (Array.isArray(nextValue) && nextValue.length === 0)) {
      nextValue = null;
    }
    pendingFilter.value[filter] = nextValue;
  }

  function clearPendingFilter() {
    if (!pendingFilter.value) {
      return;
    }
    pendingFilter.value = createEmptyFilter();
  }

  function applyPendingFilter() {
    if (pendingFilter.value) {
      viewOptions.value.filter = {
        ...createEmptyFilter(),
        ...pendingFilter.value,
      };
    }
    filterCommitDeferred.value = false;
    pendingFilter.value = null;
  }

  function discardPendingFilter() {
    filterCommitDeferred.value = false;
    pendingFilter.value = null;
  }

  function addFacilityToFilters(facilityId) {
    const target = filterCommitDeferred.value && pendingFilter.value ? pendingFilter.value : viewOptions.value.filter;
    const current = target.facility ? [...target.facility] : [];
    if (!current.includes(facilityId)) {
      current.push(facilityId);
    }
    target.facility = current;
  }

  function removeFacilityFromFilters(facilityId) {
    const target = filterCommitDeferred.value && pendingFilter.value ? pendingFilter.value : viewOptions.value.filter;
    if (!target.facility) {
      return;
    }
    const next = target.facility.filter((entry) => entry !== facilityId);
    target.facility = next.length ? next : null;
  }

  function updateFilter({
    filter,
    value,
  }) {
    if (filterCommitDeferred.value) {
      updatePendingFilter({
        filter,
        value,
      });
      return;
    }

    applyFilter({
      filter,
      value,
    });
    setPage(1);
  }

  function applyFilters() {
    applyPendingFilter();
    setPage(1);
  }

  function applyViewOptionsFromQuery(query = {}) {
    const nextPage = Number(query.page) || 1;
    const nextSort = query.sort && query.sort in SORT_COMPARATORS ? query.sort : null;
    const nextFilter = parseFilterQuery(query);
    const hasSamePage = viewOptions.value.page === nextPage;
    const hasSameSort = viewOptions.value.sort === nextSort;
    const hasSameFilter = JSON.stringify(viewOptions.value.filter) === JSON.stringify({
      ...createEmptyFilter(),
      ...nextFilter,
    });

    if (hasSamePage && hasSameSort && hasSameFilter) {
      return;
    }

    setPage(nextPage);
    setSort(nextSort);
    setFilter(nextFilter);
  }

  function markFirstPropertyAsHpa() {
    if (!properties.value.length) {
      return;
    }

    properties.value[0].hpaProperty = true;
  }

  async function pinHpaProperty(propertyId) {
    if (!propertyId || !Array.isArray(properties.value)) {
      return;
    }

    const existingProperty = properties.value.find((property) => property?.id === propertyId);
    const remainingProperties = properties.value.filter((property) => property?.id !== propertyId);

    if (existingProperty) {
      setProperties([existingProperty, ...remainingProperties]);
      markFirstPropertyAsHpa();
      return;
    }

    const {
      $api,
      $i18n,
    } = useNuxtApp();
    const { locale } = $i18n;
    const currencyStore = useCurrencyStore();
    const userCurrency = currencyStore.getCurrentCurrency;

    try {
      const callKey = `pinHpaPropertyMsApi${locale?.value}${propertyId}${userCurrency?.code}`;
      const endpoint = `property/${propertyId}/`;
      const headers = { 'Accept-Language': locale?.value };
      const query = { currency: userCurrency?.code };
      const msApiProperty = await $api.msapi(callKey, endpoint, headers, query, {});

      if (!msApiProperty?.id) {
        return;
      }

      const pinnedProperty = mapMicrositePropertyToSearchProperty(msApiProperty);
      setProperties([pinnedProperty, ...properties.value]);
    } catch (err) {
      console.error('### HPA property not found via microsite API:', err);
    }
  }

  // Restores user selections persisted to sessionStorage, e.g. after a page reload triggered by
  // the TrustArc consent banner, so the search form keeps the user's previous input.
  function restoreSearchParamsFromStorage() {
    if (false) {
      return false;
    }

    try {
      const stored = sessionStorage.getItem(SEARCH_PARAMS_STORAGE_KEY);
      if (!stored) {
        return false;
      }

      const parsed = JSON.parse(stored);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return false;
      }

      const isValidDate = (date) =>
        date
        && Number.isInteger(date.year)
        && date.year > 0
        && Number.isInteger(date.month)
        && Number.isInteger(date.day);

      if (!isValidDate(parsed.checkIn) || !isValidDate(parsed.checkOut) || !parsed.location) {
        return false;
      }

      const { today } = useDateTime();
      const todayDate = new Date(today.year, today.month - 1, today.day);
      const checkInDate = new Date(parsed.checkIn.year, parsed.checkIn.month - 1, parsed.checkIn.day);
      const checkOutDate = new Date(parsed.checkOut.year, parsed.checkOut.month - 1, parsed.checkOut.day);

      if (Number.isNaN(checkInDate.getTime()) || checkInDate < todayDate) {
        return false;
      }

      if (Number.isNaN(checkOutDate.getTime()) || checkOutDate <= checkInDate) {
        return false;
      }

      // Whitelist + coerce restored fields so a corrupt payload cannot inject unknown keys or
      // wrong types (e.g. a non-array groupAge would crash addSearchGroupAge's .push()).
      const isPlainObject = (value) =>
        value && typeof value === 'object' && !Array.isArray(value);

      // Recompute the derived disabled-date range from the restored checkIn so we never
      // trust the persisted values for these fields.
      const checkInStart = new Date(parsed.checkIn.year, parsed.checkIn.month - 1, parsed.checkIn.day);
      const disabledUntilDate = addDays(checkInStart, 1);
      const disabledFromDate = addDays(checkInStart, MAX_NUM_NIGHTS);

      // Single atomic mutation: the WDS date picker reacts to props synchronously and a
      // two-step (checkOut then checkIn) restore can race with its internal auto-adjust logic.
      searchParams.value = {
        ...searchParams.value,
        location: isPlainObject(parsed.location) ? parsed.location : {},
        checkIn: parsed.checkIn,
        checkOut: parsed.checkOut,
        disabledUntil: {
          year: disabledUntilDate.getFullYear(),
          month: disabledUntilDate.getMonth() + 1,
          day: disabledUntilDate.getDate(),
        },
        disabledFrom: {
          year: disabledFromDate.getFullYear(),
          month: disabledFromDate.getMonth() + 1,
          day: disabledFromDate.getDate(),
        },
        guests: Number.isInteger(parsed.guests) ? parsed.guests : searchParams.value.guests,
        groupType: isPlainObject(parsed.groupType) ? parsed.groupType : {},
        groupAge: Array.isArray(parsed.groupAge) ? parsed.groupAge : [],
        phrase: typeof parsed.phrase === 'string' ? parsed.phrase : '',
      };
      isSearchActivated.value = true;
      return true;
    } catch (err) {
      console.error('### Unable to restore search form params from sessionStorage:', err);
      return false;
    } finally {
      // Single-shot: clear once consumed so it does not leak into later navigations or refreshes.
      try {
        sessionStorage.removeItem(SEARCH_PARAMS_STORAGE_KEY);
      } catch (err) {
        console.error('### Unable to clear search form params from sessionStorage:', err);
      }
    }
  }

  // Persists the current search selections to sessionStorage so they survive an imminent
  // page reload (e.g. one triggered by the TrustArc consent banner). Only writes when the
  // user has actually interacted with the form, to avoid storing untouched SSR defaults.
  function persistForReload() {
    if (false || !isSearchActivated.value) {
      return;
    }

    try {
      sessionStorage.setItem(SEARCH_PARAMS_STORAGE_KEY, JSON.stringify(searchParams.value));
    } catch (err) {
      console.error('### Unable to persist search form params to sessionStorage:', err);
    }
  }

  return {
    searchParams,
    isSearchActivated,
    mapSelectedProperty,
    properties,
    location,
    sortOrderRank,
    viewOptions,
    filterCommitDeferred,
    pendingFilter,
    maxPriceAmongProperties,
    getSearchLocation,
    getSearchPhrase,
    getSearchCheckIn,
    getSearchCheckOut,
    getSearchDisabledUntil,
    getSearchDisableFrom,
    getSearchNumberOfGuests,
    getSearchGroupType,
    getSearchGroupAge,
    getIsSearchActivated,
    getMapSelectedProperty,
    getProperties,
    getInventoryLocation,
    getSortOrderRank,
    getViewOptions,
    currentFilter,
    filteredHWProperties,
    filteredProperties,
    maxPriceAvailable,
    numFiltersApplied,
    numCurrentFiltersApplied,
    districts,
    setSearchParam,
    setSearchCheckIn,
    setSearchCheckOut,
    addSearchGroupAge,
    setSearchActivated,
    sendSearchActivatedTracking,
    setMapSelectedProperty,
    setProperties,
    setLocation,
    setSortOrderRank,
    setPage,
    setSort,
    setFilter,
    applyFilter,
    clearFilter,
    setFilterCommitDeferred,
    initPendingFilter,
    updatePendingFilter,
    clearPendingFilter,
    applyPendingFilter,
    discardPendingFilter,
    addFacilityToFilters,
    removeFacilityFromFilters,
    updateFilter,
    applyFilters,
    applyViewOptionsFromQuery,
    markFirstPropertyAsHpa,
    pinHpaProperty,
    restoreSearchParamsFromStorage,
    persistForReload,
  };
});
